<!-- What to do before the scanner opens. THIS FILE IS YOURS.

     RoomPlan puts up its own coaching once it is running -- "move closer", "point at the
     wall" -- and none of it says the thing that decides whether the scan is any good, which
     is that you have to walk the WHOLE room in one go. Somebody who scans three walls and
     presses Done gets told the room did not close, which is true and unhelpful, because by
     then they have put the phone down.

     Five lines and a picture, shown once. The tick remembers. -->
<template>
  <div class="scrim" @click.self="$emit('close')">
    <section class="card" role="dialog" aria-modal="true" aria-labelledby="scan-title">
      <h1 id="scan-title">Scanning the room</h1>
      <p class="lede">About a minute. Your phone measures the walls with its LiDAR sensor.</p>

      <ol>
        <li><b>Walk the whole room.</b> All the way round, back to where you started. If you
          stop half way it cannot close the room and the measurements are no use.</li>
        <li><b>Point at the walls, corner to corner.</b> Sweep slowly, floor to ceiling. It is
          the corners it needs most.</li>
        <li><b>Stand about a metre back.</b> Too close and it loses the wall behind what you
          are pointing at.</li>
        <li><b>Include the cupboards and the bench.</b> It picks them up, and if there is a
          kitchen there already it will be put back in for you.</li>
        <li><b>Open the blinds.</b> It works in the dark but it works better in the light.</li>
      </ol>

      <p class="fine">
        Sizes come back to the nearest centimetre or so. Check anything that matters against
        a tape before it goes to {{ maker.name }}.
      </p>

      <label class="again">
        <input type="checkbox" v-model="hide" />
        <span>Do not show this again</span>
      </label>

      <button class="go" type="button" @click="$emit('start', hide)">Start scanning</button>
      <button class="quiet" type="button" @click="$emit('close')">Not now</button>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue';

defineProps({ maker: Object });
defineEmits(['close', 'start']);

const hide = ref(false);
</script>

<style scoped>
.scrim {
  position: fixed; inset: 0; z-index: 60;
  background: color-mix(in oklab, var(--k-ink) 46%, transparent);
  display: flex; align-items: flex-end; justify-content: center;
}
@media (min-width: 700px) { .scrim { align-items: center; padding: 24px; } }
.card {
  width: 100%; max-width: 460px; max-height: 92vh; overflow-y: auto;
  background: var(--k-paper); border-radius: 20px 20px 0 0;
  padding: 22px 20px calc(20px + env(safe-area-inset-bottom));
}
@media (min-width: 700px) { .card { border-radius: 16px; } }
h1 { margin: 0 0 6px; font: 600 20px/1.25 var(--ion-font-family, system-ui); color: var(--k-ink); letter-spacing: -.015em; }
.lede { margin: 0 0 16px; font: 400 14px/1.5 var(--ion-font-family, system-ui); color: var(--k-mute); }
ol { margin: 0 0 14px; padding: 0; list-style: none; counter-reset: step; display: grid; gap: 11px; }
ol li {
  counter-increment: step; position: relative; padding-left: 32px;
  font: 400 13.5px/1.45 var(--ion-font-family, system-ui); color: var(--k-dim);
}
ol li::before {
  content: counter(step); position: absolute; left: 0; top: 0;
  width: 22px; height: 22px; display: grid; place-items: center; border-radius: 999px;
  background: var(--k-ink); color: var(--k-paper);
  font: 600 11.5px/1 var(--ion-font-family, system-ui);
}
ol b { font-weight: 600; color: var(--k-ink); }
.fine { margin: 0 0 14px; font: 400 12px/1.45 var(--ion-font-family, system-ui); color: var(--k-mute); }
.again { display: flex; align-items: center; gap: 9px; margin: 0 0 14px; font: 400 13px/1 var(--ion-font-family, system-ui); color: var(--k-dim); cursor: pointer; }
.again input { width: 18px; height: 18px; accent-color: var(--k-ink); }
.go {
  width: 100%; border: 0; border-radius: 12px; cursor: pointer;
  background: var(--k-ink); color: var(--k-paper);
  font: 600 15px/1 var(--ion-font-family, system-ui);
  padding: 16px; min-height: 54px;
}
.quiet {
  width: 100%; margin-top: 6px; border: 0; background: none; cursor: pointer;
  color: var(--k-mute); font: 500 13px/1 var(--ion-font-family, system-ui);
  padding: 12px; min-height: 44px;
}
</style>
